import { useMemo } from 'react';
import type { UserRole } from '@/models/user';
import { useAuthUser } from './useAuthUser';

/**
 * UI-only role gating. The role lives in localStorage — server must enforce
 * anything that actually persists. `canFinalize` stays true on a FINAL report
 * so physicians can still open an addendum.
 */
export interface Permissions {
  role: UserRole | null;
  canEdit: boolean;
  canFinalize: boolean;
}

interface UsePermissionsOptions {
  isFinalized?: boolean;
}

const KNOWN_ROLES: ReadonlySet<UserRole> = new Set([
  'ADMIN',
  'PHYSICIAN',
  'TECHNICIAN',
]);

function normalizeRole(raw: string | null | undefined): UserRole | null {
  if (!raw) return null;
  const upper = raw.toUpperCase();
  return KNOWN_ROLES.has(upper as UserRole) ? (upper as UserRole) : null;
}

export function usePermissions(opts?: UsePermissionsOptions): Permissions {
  const { user } = useAuthUser();
  const isFinalized = opts?.isFinalized ?? false;
  return useMemo(() => {
    const role = normalizeRole(user.role);
    const roleCanEdit = role === 'PHYSICIAN' || role === 'TECHNICIAN';
    return {
      role,
      canEdit: roleCanEdit && !isFinalized,
      canFinalize: role === 'PHYSICIAN',
    };
  }, [user.role, isFinalized]);
}
